import { NavLink, useNavigate } from "react-router-dom";
import {
  Home,
  MessageSquareWarning,
  MessagesSquare,
  BookOpen,
  LifeBuoy,
  Bot,
  Palette,
  Globe,
  LogOut,
  type LucideIcon,
} from "lucide-react";
import { Logo } from "./Logo";
import { cn } from "@/lib/cn";
import { clearSession } from "@/lib/session";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: number;
}

interface SidebarProps {
  campgroundName?: string;
  gapsCount?: number;
  ticketsCount?: number;
  /** Off-canvas open state on mobile (< md). Ignored on desktop where the sidebar is always static. */
  open?: boolean;
  /** Called after a nav item is tapped so the mobile drawer can close itself. */
  onNavigate?: () => void;
}

export function Sidebar({ campgroundName, gapsCount, ticketsCount, open = false, onNavigate }: SidebarProps) {
  const nav = useNavigate();

  const primary: NavItem[] = [
    { to: "/", label: "Home", icon: Home },
    { to: "/gaps", label: "Content gaps", icon: MessageSquareWarning, badge: gapsCount },
    { to: "/conversations", label: "Conversations", icon: MessagesSquare },
    { to: "/tickets", label: "Tickets", icon: LifeBuoy, badge: ticketsCount },
    { to: "/knowledge", label: "Knowledge", icon: BookOpen },
  ];
  const settings: NavItem[] = [
    { to: "/settings/bot", label: "Bot", icon: Bot },
    { to: "/settings/widget", label: "Widget", icon: Palette },
    { to: "/settings/domains", label: "Domains", icon: Globe },
  ];

  return (
    <aside
      className={cn(
        "flex h-screen w-60 shrink-0 flex-col",
        "bg-ink text-white/85 border-r border-ink-3",
        // Mobile (< md): off-canvas drawer that slides in over the content.
        // Desktop (>= md): static column, always visible, no transform.
        "fixed inset-y-0 left-0 z-50 transition-transform md:static md:z-auto md:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full",
      )}
    >
      {/* Logo block — keep its own internal padding because it's standalone. */}
      <div className="px-5 pt-6 pb-5">
        <Logo className="h-8 [&_svg]:h-full [&_svg]:w-auto" />
      </div>

      {/* Nav: no horizontal padding on the nav container itself so the active
          pill can extend edge-to-edge. Section labels and rows get their own
          inner padding. */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin">
        <ul>
          {primary.map((item) => (
            <SidebarItem key={item.to} item={item} onNavigate={onNavigate} />
          ))}
        </ul>

        <SectionLabel>Settings</SectionLabel>
        <ul>
          {settings.map((item) => (
            <SidebarItem key={item.to} item={item} onNavigate={onNavigate} />
          ))}
        </ul>
      </nav>

      <div className="border-t border-ink-3">
        <div className="px-5 py-3">
          <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">Campground</div>
          <div className="mt-1 truncate text-sm font-medium text-white">
            {campgroundName ?? "—"}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            clearSession();
            nav("/login", { replace: true });
          }}
          className={cn(
            "flex w-full items-center gap-2.5 px-5 py-3 text-sm",
            "text-white/70 hover:bg-ink-2 hover:text-white transition-colors",
          )}
        >
          <LogOut size={16} strokeWidth={1.75} />
          Sign out
        </button>
      </div>
    </aside>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-5 mb-1 px-5 text-[10px] uppercase tracking-[0.14em] text-white/40">
      {children}
    </div>
  );
}

function SidebarItem({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        to={item.to}
        end={item.to === "/"}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            // Full-bleed row: no rounded corners, no horizontal margin —
            // padding lives inside so the coral background spans edge-to-edge.
            "group flex w-full items-center justify-between gap-2.5 px-5 py-2.5 text-sm transition-colors",
            isActive
              ? "bg-coral text-white"
              : "text-white/70 hover:bg-ink-2 hover:text-white",
          )
        }
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <Icon size={16} strokeWidth={1.75} className="shrink-0" />
          <span className="truncate font-normal">{item.label}</span>
        </span>
        {item.badge !== undefined && item.badge > 0 && (
          <span
            className={cn(
              "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] tabular-nums",
              "bg-white/15 text-white",
            )}
          >
            {item.badge > 99 ? "99+" : item.badge}
          </span>
        )}
      </NavLink>
    </li>
  );
}
